// src/features/system/iam/hooks/useIAMUsers.ts
//
// React Query hooks for IAM User Management.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchIAMUsers,
  fetchIAMUser,
  createIAMUser,
  provisionIAMUser,
  updateIAMProfile,
  changeIAMPassword,
  deactivateIAMUser,
  activateIAMUser,
  updateIAMRoles,
  invalidateIAMSessions,
  deleteIAMUser,
  fetchIAMRoles,
  updateRolePermissions,
  type IAMUser,
  type IAMRole,
  type IAMUserListResponse,
  type IAMRolesResponse,
  type CreateUserPayload,
  type ProvisionUserPayload,
  type UpdateProfilePayload,
  type ChangePasswordPayload,
} from '../api/iamApi';

const USERS_KEY = ['iam-users'] as const;
const ROLES_KEY = ['iam-roles'] as const;

/* ─── List users ──────────────────────────────────────────── */

export function useIAMUsers(opts: {
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
  enabled?: boolean;
} = {}) {
  return useQuery<IAMUserListResponse, Error>({
    queryKey: [...USERS_KEY, opts.status, opts.search, opts.limit, opts.offset],
    queryFn: () => fetchIAMUsers(opts),
    staleTime: 15_000,
    enabled: opts.enabled !== false,
  });
}

/* ─── Get single user ─────────────────────────────────────── */

export function useIAMUser(idUser: number | null | undefined) {
  return useQuery<IAMUser, Error>({
    queryKey: [...USERS_KEY, idUser],
    queryFn: () => fetchIAMUser(idUser!),
    staleTime: 15_000,
    enabled: !!idUser,
  });
}

/* ─── Create user ─────────────────────────────────────────── */

export function useCreateIAMUser() {
  const qc = useQueryClient();
  return useMutation<IAMUser, Error, CreateUserPayload>({
    mutationFn: createIAMUser,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

/* ─── Provision federated user (superadmin tool) ──────────── */

export function useProvisionIAMUser() {
  const qc = useQueryClient();
  return useMutation<IAMUser, Error, ProvisionUserPayload>({
    mutationFn: provisionIAMUser,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

/* ─── Update profile ──────────────────────────────────────── */

export function useUpdateIAMProfile() {
  const qc = useQueryClient();
  return useMutation<IAMUser, Error, { idUser: number } & UpdateProfilePayload>({
    mutationFn: ({ idUser, ...payload }) => updateIAMProfile(idUser, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

/* ─── Change password ─────────────────────────────────────── */

export function useChangeIAMPassword() {
  return useMutation<{ password_changed: boolean }, Error, { idUser: number } & ChangePasswordPayload>({
    mutationFn: ({ idUser, ...payload }) => changeIAMPassword(idUser, payload),
  });
}

/* ─── Deactivate / Activate ───────────────────────────────── */

export function useDeactivateIAMUser() {
  const qc = useQueryClient();
  return useMutation<IAMUser, Error, number>({
    mutationFn: deactivateIAMUser,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export function useActivateIAMUser() {
  const qc = useQueryClient();
  return useMutation<IAMUser, Error, number>({
    mutationFn: activateIAMUser,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

/* ─── Update roles ────────────────────────────────────────── */

export function useUpdateIAMRoles() {
  const qc = useQueryClient();
  return useMutation<IAMUser, Error, { idUser: number; roles: string[] }>({
    mutationFn: ({ idUser, roles }) => updateIAMRoles(idUser, roles),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

/* ─── Invalidate sessions (user/tenant) ─────────────────── */

export function useInvalidateIAMSessions() {
  const qc = useQueryClient();
  return useMutation<
    { scope: 'user' | 'tenant'; id_user?: number; tenant_id?: number; updated_sessions: number },
    Error,
    { scope: 'user' | 'tenant'; targetId: number }
  >({
    mutationFn: ({ scope, targetId }) => invalidateIAMSessions(scope, targetId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

/* ─── Delete user ─────────────────────────────────────────── */

export function useDeleteIAMUser() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: deleteIAMUser,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

/* ─── List roles ──────────────────────────────────────────── */

export function useIAMRoles(enabled = true) {
  return useQuery<IAMRolesResponse, Error>({
    queryKey: [...ROLES_KEY],
    queryFn: fetchIAMRoles,
    staleTime: 30_000,
    enabled,
  });
}

/* ─── Set role permissions ────────────────────────────────── */

export function useSetRolePermissions() {
  const qc = useQueryClient();
  return useMutation<IAMRole, Error, { roleKey: string; permissions: string[] }>({
    mutationFn: ({ roleKey, permissions }) => updateRolePermissions(roleKey, permissions),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
}